<!-- Liste des clients standard (admin) : pagination et suppression. Aligné DA Patrimonea (unified-table). -->
<template>
  <div class="client-standard-list py-3">
    <div class="unified-table-container">
      <div class="csl-head">
        <h3 class="csl-title">Utilisateurs (Clients)</h3>
        <span class="csl-badge">{{ users.length }} utilisateurs</span>
      </div>

      <div v-if="loading" class="csl-loading">
        <span class="csl-spinner" aria-hidden="true" />
        <span>Chargement…</span>
      </div>

      <div v-else-if="error" class="alert alert-danger mb-0" role="alert">
        <strong v-if="errorCode" class="d-block small text-uppercase mb-1">{{ errorCode }}</strong>
        {{ error }}
      </div>

      <div v-else-if="users.length === 0" class="empty-state-card">
        <p class="text-muted mb-0">Aucun utilisateur</p>
      </div>

      <template v-else>
        <div class="table-controls csl-controls">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <label for="page-size" class="mb-0 small text-muted">Afficher</label>
            <select
              id="page-size"
              v-model.number="itemsPerPage"
              class="form-select form-select-sm csl-page-size"
              @change="handlePageSizeChange"
            >
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
            <span class="small text-muted">lignes</span>
          </div>
          <small class="text-muted">{{ startItemIndex }} - {{ endItemIndex }} sur {{ users.length }}</small>
        </div>

        <div v-if="isMobile" class="csl-cards">
          <div
            v-for="(user, idx) in paginatedUsers"
            :key="`${currentPage}-${idx}-${user.login || 'no-login'}-${user.email || 'no-email'}`"
            class="csl-card"
          >
            <div class="csl-card__block">
              <span class="csl-card__label">Login</span>
              <span class="csl-card__value">{{ user.login || user.username || '—' }}</span>
            </div>
            <div class="csl-card__block">
              <span class="csl-card__label">Email</span>
              <span class="csl-card__value">{{ user.email || '—' }}</span>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-danger w-100 mt-2"
              :disabled="deletingId === user.login"
              @click="confirmDelete(user)"
            >
              <span
                v-if="deletingId === user.login"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Supprimer
            </button>
          </div>
        </div>

        <div v-else class="table-scroll-area">
          <table
            class="unified-table csl-table mb-0"
            :key="`users-table-${currentPage}-${itemsPerPage}-${users.length}`"
          >
            <thead>
              <tr>
                <th>Login</th>
                <th>Email</th>
                <th class="text-end csl-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(user, idx) in paginatedUsers"
                :key="`${currentPage}-${idx}-${user.login || 'no-login'}-${user.email || 'no-email'}`"
              >
                <td>{{ user.login || user.username || '—' }}</td>
                <td>{{ user.email }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="deletingId === user.login"
                    @click="confirmDelete(user)"
                  >
                    <span
                      v-if="deletingId === user.login"
                      class="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                    Supprimer
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-wrap mt-2 csl-pagination">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="currentPage === 1"
            @click.stop.prevent="goToPreviousPage"
          >
            Précédent
          </button>
          <template v-if="!isMobile">
            <button
              v-for="(page, index) in visiblePages"
              :key="`page-${page}-${index}`"
              type="button"
              class="btn btn-sm csl-page-btn"
              :class="{ 'csl-page-btn--active': page === currentPage }"
              @click.stop.prevent="goToPage(page)"
            >
              {{ page }}
            </button>
          </template>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="currentPage === totalPages"
            @click.stop.prevent="goToNextPage"
          >
            Suivant
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  ADMIN_CLIENT_DELETE_CODES,
  formatAdminClientDeleteError,
} from '@/utils/errorMessages';
import { clearAuthenticatedSession, isAuthenticatedSession } from '@/utils/sessionAuth';

export default {
  name: 'ClientStandardList',
  data() {
    return {
      users: [],
      isMobile: false,
      currentPage: 1,
      itemsPerPage: 10,
      pageSizeOptions: [10, 20, 30, 50, 100],
      loading: false,
      error: '',
      errorCode: '',
      deletingId: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.users.length / this.itemsPerPage));
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.users.slice(start, end);
    },
    startItemIndex() {
      if (this.users.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endItemIndex() {
      return Math.min(this.currentPage * this.itemsPerPage, this.users.length);
    },
    visiblePages() {
      const pages = [];
      if (this.totalPages <= 0) return pages;

      const maxButtons = 5;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxButtons - 1);

      if (end - start + 1 < maxButtons) {
        start = Math.max(1, end - maxButtons + 1);
      }

      for (let page = start; page <= end; page += 1) {
        if (Number.isInteger(page) && page >= 1 && page <= this.totalPages) {
          pages.push(page);
        }
      }
      return pages;
    },
  },
  watch: {
    itemsPerPage() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    await this.fetchUsers();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    },
    handlePageSizeChange() {
      this.currentPage = 1;
    },
    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    goToPage(page) {
      const target = Math.min(Math.max(1, page), this.totalPages);
      this.currentPage = target;
    },
    async fetchUsers() {
      if (!isAuthenticatedSession()) {
        this.redirectToLogin();
        return;
      }

      this.loading = true;
      this.error = '';
      this.errorCode = '';

      try {
        const response = await axios.get('/api/clients-standard/');

        const payload = response.data;
        const items = Array.isArray(payload) ? payload : payload?.results || payload?.data || [];

        this.users = items.map((item) => ({
          nom: item.nom || '',
          prenom: item.prenom || '',
          login: item.login || item.username || '',
          email: item.email || '',
        }));
        this.currentPage = 1;
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.redirectToLogin();
        } else {
          console.error('Erreur lors du chargement des utilisateurs:', err);
          this.error = 'Erreur lors du chargement des utilisateurs.';
        }
      } finally {
        this.loading = false;
      }
    },
    redirectToLogin() {
      clearAuthenticatedSession();
      if (this.$router) {
        this.$router.push('/connexion');
      } else {
        window.location.href = '/connexion';
      }
    },
    async confirmDelete(user) {
      const login = (user?.login || '').trim();
      if (!login) return;
      const label = user.login || user.username || user.email || login;
      const ok = window.confirm(`Supprimer définitivement le client ${label} ? Cette action est irréversible.`);
      if (!ok) return;
      await this.deleteClient(login);
    },
    async deleteClient(clientLogin) {
      if (!isAuthenticatedSession()) {
        this.redirectToLogin();
        return;
      }
      this.deletingId = clientLogin;
      try {
        await axios.delete(`/api/admin/clients/${encodeURIComponent(clientLogin)}/delete/`, {
          timeout: 30000,
        });
        this.users = this.users.filter((u) => u.login !== clientLogin);
        if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
        this.error = '';
        this.errorCode = '';
      } catch (error) {
        const data = error.response?.data || {};
        const headerCode = error.response?.headers?.['x-patrimonea-error-code']
          || error.response?.headers?.['X-Patrimonea-Error-Code'];
        const apiCode = data.code || headerCode || '';
        this.errorCode = apiCode
          ? `${apiCode}${ADMIN_CLIENT_DELETE_CODES[apiCode] ? ` — ${ADMIN_CLIENT_DELETE_CODES[apiCode]}` : ''}`
          : (error.response?.status ? `HTTP ${error.response.status}` : 'Erreur suppression');
        this.error = formatAdminClientDeleteError(error, { includeCode: false });
        console.error('Suppression client échouée', {
          login: clientLogin,
          status: error.response?.status,
          code: apiCode,
          headerCode,
          meta: data.meta,
          hint: data.hint,
          contentType: error.response?.headers?.['content-type'],
        });
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style scoped>
.client-standard-list {
  min-width: 0;
}

.csl-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.csl-title {
  margin: 0;
  font-family: var(--font-title);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--bank-navy);
}

.csl-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--bank-gray-700);
  background: color-mix(in srgb, var(--bank-navy) 6%, var(--bank-white));
  border: 1px solid color-mix(in srgb, var(--bank-navy) 10%, var(--bank-gray-200));
}

.csl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 200px;
  color: var(--bank-gray-500);
  font-weight: 500;
}

.csl-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--bank-gray-200);
  border-top-color: var(--bank-gold);
  border-radius: 50%;
  animation: csl-spin 0.85s linear infinite;
}

@keyframes csl-spin {
  to {
    transform: rotate(360deg);
  }
}

.csl-controls {
  margin-bottom: 12px;
}

.csl-page-size {
  width: auto;
  min-width: 5.5rem;
}

.csl-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.csl-card {
  border: 1px solid color-mix(in srgb, var(--bank-navy) 8%, var(--bank-gray-200));
  border-radius: 12px;
  padding: 1rem;
  background: var(--bank-white);
  box-shadow: 0 1px 3px rgba(10, 26, 47, 0.05);
}

.csl-card__block {
  margin-bottom: 0.65rem;
}

.csl-card__label {
  display: block;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--bank-gray-500);
  margin-bottom: 0.2rem;
}

.csl-card__value {
  font-size: 0.9375rem;
  color: var(--bank-gray-900);
  word-break: break-word;
}

.csl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.csl-table th {
  text-align: left;
  padding: 10px 12px;
  background: var(--brand-navy);
  color: var(--paper);
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: none;
}

.csl-table td {
  padding: 12px;
  border-bottom: 1px solid var(--bank-gray-200);
  vertical-align: middle;
}

.csl-th-actions {
  width: 8rem;
}

.csl-pagination {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  align-items: center;
}

.csl-page-btn {
  border-color: var(--bank-gray-300);
  color: var(--bank-gray-700);
  background: var(--bank-white);
  font-weight: 600;
}

.csl-page-btn:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--bank-gold) 40%, var(--bank-gray-300));
  color: var(--bank-navy);
  background: color-mix(in srgb, var(--bank-white) 92%, var(--bank-gold) 6%);
}

.csl-page-btn--active {
  border-color: var(--bank-gold-dark) !important;
  color: var(--bank-navy) !important;
  background: linear-gradient(135deg, color-mix(in srgb, var(--bank-gold) 22%, var(--bank-white)) 0%, var(--bank-gold-light) 100%) !important;
  box-shadow: 0 6px 16px rgba(10, 26, 47, 0.1);
}

.empty-state-card {
  text-align: center;
  padding: 2.5rem 1.5rem;
  background: color-mix(in srgb, var(--bank-navy) 2.5%, var(--bank-white));
  border-radius: 14px;
  border: 1px dashed var(--bank-gray-300);
}

@media (max-width: 768px) {
  .csl-pagination .btn {
    flex: 1 1 auto;
    min-height: 42px;
  }
}
</style>
